import { useMemo, useState } from "react";
import { Check, Minus, X, Search, Lock, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { groupCatalog, filterGroups, STATE_LABEL } from "@/lib/access";

const CELL_STYLE = {
  none: "border-slate-200 bg-white text-slate-300 hover:border-primary/50 hover:text-primary",
  inherited: "border-slate-200 bg-slate-100 text-slate-500 hover:border-rose-300 hover:text-rose-500",
  granted: "border-primary bg-primary text-white hover:bg-primary/85",
  revoked: "border-rose-300 bg-rose-50 text-rose-600 hover:bg-rose-100",
};

export function PermCell({ state, onClick, disabled, testid, title, locked }) {
  const Icon = state === "revoked" ? X : state === "none" ? Minus : Check;
  return (
    <button type="button" data-testid={testid} data-state={state} disabled={disabled || locked}
      onClick={onClick} title={title || STATE_LABEL[state]}
      className={`mx-auto flex h-6 w-6 items-center justify-center rounded-md border text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${CELL_STYLE[state]}`}>
      {locked ? <Lock className="h-3 w-3" /> : <Icon className="h-3.5 w-3.5" />}
    </button>
  );
}

export function PermLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500" data-testid="perm-legend">
      <span className="flex items-center gap-1"><span className="flex h-4 w-4 items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-500"><Check className="h-2.5 w-2.5" /></span> From audience defaults</span>
      <span className="flex items-center gap-1"><span className="flex h-4 w-4 items-center justify-center rounded border border-primary bg-primary text-white"><Check className="h-2.5 w-2.5" /></span> Granted here</span>
      <span className="flex items-center gap-1"><span className="flex h-4 w-4 items-center justify-center rounded border border-rose-300 bg-rose-50 text-rose-600"><X className="h-2.5 w-2.5" /></span> Revoked here</span>
      <span className="flex items-center gap-1"><span className="flex h-4 w-4 items-center justify-center rounded border border-slate-200 bg-white text-slate-300"><Minus className="h-2.5 w-2.5" /></span> Not granted</span>
    </div>
  );
}

export function usePermFilter(catalog) {
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [modules, setModules] = useState([]);
  const groups = useMemo(() => groupCatalog(catalog), [catalog]);
  const shown = useMemo(() => filterGroups(groups, { group, q, modules }), [groups, group, q, modules]);
  return { group, setGroup, q, setQ, modules, setModules, groups, shown };
}

/** Multi-select module picker: check any number of modules across the groups. */
function ModulePicker({ groups, group, modules, setModules, testid }) {
  const [open, setOpen] = useState(false);
  const [needle, setNeedle] = useState("");
  const visible = groups.filter((g) => !group || g.id === group);
  const n = needle.trim().toLowerCase();
  const toggle = (id) => setModules(modules.includes(id) ? modules.filter((x) => x !== id) : [...modules, id]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1 text-xs font-normal" data-testid={`${testid}-modules`}>
          {modules.length ? `${modules.length} module${modules.length === 1 ? "" : "s"}` : "All modules"}
          <ChevronsUpDown className="h-3 w-3 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`${testid}-modules-panel`}>
        <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
          <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <input value={needle} onChange={(e) => setNeedle(e.target.value)} placeholder="Filter modules…"
            className="h-6 w-full bg-transparent text-xs outline-none" data-testid={`${testid}-modules-search`} />
          {modules.length > 0 && (
            <button type="button" onClick={() => setModules([])} className="shrink-0 text-[11px] text-primary hover:underline"
              data-testid={`${testid}-modules-clear`}>Clear</button>
          )}
        </div>
        <div className="max-h-64 overflow-auto py-1">
          {visible.map((g) => {
            const rows = g.modules.filter((m) => !n || m.label.toLowerCase().includes(n) || m.id.toLowerCase().includes(n));
            if (!rows.length) return null;
            return (
              <div key={g.id}>
                <p className="px-2 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</p>
                {rows.map((m) => (
                  <label key={m.id} className="flex cursor-pointer items-center gap-2 px-2 py-1 text-xs hover:bg-muted"
                    data-testid={`${testid}-module-opt-${m.id}`}>
                    <Checkbox checked={modules.includes(m.id)} onCheckedChange={() => toggle(m.id)} className="h-3.5 w-3.5" />
                    <span className="truncate text-slate-700" title={m.id}>{m.label}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PermFilters({ filter, testid = "perm-filter", children }) {
  const { group, setGroup, q, setQ, modules, setModules, groups, shown } = filter;
  const count = shown.reduce((n, g) => n + g.modules.reduce((m, x) => m + x.actions.length, 0), 0);
  const dirty = Boolean(q || group || modules.length);
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid={testid}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a module, app or permission…"
          data-testid={`${testid}-search`} className="h-8 w-64 pl-7 pr-7 text-xs" />
        {q && (
          <button type="button" onClick={() => setQ("")} data-testid={`${testid}-search-clear`}
            className="absolute right-1.5 top-1.5 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      <Select value={group || "all"} onValueChange={(v) => setGroup(v === "all" ? "" : v)}>
        <SelectTrigger className="h-8 w-44 text-xs" data-testid={`${testid}-group`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All groups</SelectItem>
          {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <ModulePicker groups={groups} group={group} modules={modules} setModules={setModules} testid={testid} />
      {dirty && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" data-testid={`${testid}-reset`}
          onClick={() => { setQ(""); setGroup(""); setModules([]); }}>Reset</Button>
      )}
      {children}
      <span className="ml-auto text-xs text-muted-foreground" data-testid={`${testid}-summary`}>{count} permission{count === 1 ? "" : "s"}</span>
    </div>
  );
}

/** Grouped permission rows (module × action) against arbitrary columns.
 *  `renderCell(perm, col, module)` returns the cell content. */
export function PermTable({ groups, columns, renderCell, testid = "perm-table", maxHeight = "max-h-[65vh]", firstColLabel = "Module / Action" }) {
  return (
    <div className={`${maxHeight} overflow-auto rounded-lg border border-slate-200`} data-testid={testid}>
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-20">
          <tr className="text-left">
            <th className="sticky left-0 z-30 border-b border-border bg-card px-3 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{firstColLabel}</th>
            {columns.map((c) => (
              <th key={c.id} className="border-b border-border bg-card px-2 py-2 text-center text-xs font-medium text-muted-foreground whitespace-nowrap" title={c.title || c.label}>
                <div className="max-w-[110px] truncate mx-auto">{c.label}</div>
                {c.sub != null && <div className="text-[10px] font-normal text-slate-400">{c.sub}</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 && (
            <tr><td colSpan={columns.length + 1} className="py-8 text-center text-sm text-slate-400" data-testid={`${testid}-empty`}>No permissions match.</td></tr>
          )}
          {groups.map((g) => (
            <GroupRows key={g.id} group={g} columns={columns} renderCell={renderCell} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupRows({ group, columns, renderCell }) {
  return (
    <>
      <tr>
        <td colSpan={columns.length + 1} className="sticky left-0 border-b border-border bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-testid={`perm-group-${group.id}`}>
          {group.label}
        </td>
      </tr>
      {group.modules.map((m) => m.actions.map((act, i) => {
        const perm = `${m.id}.${act}`;
        return (
          <tr key={perm} className="group hover:bg-muted/60" data-testid={`perm-row-${perm}`}>
            <td className="sticky left-0 z-10 border-b border-border bg-card px-3 py-1.5 whitespace-nowrap group-hover:bg-muted">
              {i === 0 ? (
                <span className="font-medium text-slate-800" title={m.id}>{m.label}</span>
              ) : (
                <span className="inline-block w-3" />
              )}
              <span className="ml-1.5 text-xs text-slate-400">· {act}</span>
              {i === 0 && m.apps?.length > 0 && m.group !== "features" && (
                <span className="ml-1.5 text-[10px] text-slate-400">({m.apps.map((a) => a.name).join(", ")})</span>
              )}
            </td>
            {columns.map((c) => (
              <td key={c.id} className="border-b border-border px-2 py-1.5 text-center">{renderCell(perm, c, m)}</td>
            ))}
          </tr>
        );
      }))}
    </>
  );
}
